<template>
	<div
		class="special-building-pack-left-box"
		v-if="
			specialBuildingPack &&
			!tutorialActive &&
			!hiddenLeftBoxOffers.includes(specialBuildingPack.store_id)
		"
	>
		<div
			class="special-building-pack-left-box__item"
			:class="[getPremiumLeftBoxClass(specialBuildingPack)]"
			@click="openPopup"
		>
			<div
				v-if="
					specialBuildingPack.attributes.type ===
					OfferType.SPECIAL_BUILDING
				"
				class="special-building-icon"
				:class="`icon-${
					specialBuildingPack?.attributes.building_image
				}-60`"
			/>
			<Timer
				v-if="
					specialBuildingPack.expired_at &&
					specialBuildingPack.expired_at > 0
				"
				class="special-building-pack-left-box__timer"
				:time="
					convertUnixTimeToRemainingSeconds(
						specialBuildingPack.expired_at,
					)
				"
				:has-icon="false"
				@timerFinished="handleLeftboxEnd"
			/>
			<span
				v-if="specialBuildingPack.attributes.multiplier_value"
				class="special-building-pack-left-box__text"
			>
				{{
					specialBuildingPack.attributes.multiplier_value + "x Value"
				}}
			</span>
		</div>
	</div>
</template>

<script lang="ts" setup>
import SpecialBuildingPackPopup from "@/components/shop/marketplace/SpecialBuildingPackPopup.vue";
import Timer from "@/components/Timer.vue";
import { SPECIAL_BUILDING_PACK_COOLDOWN } from "@/config/constants";
import type { InGameShopItem } from "@/store/useOfferStore";
import useOfferStore from "@/store/useOfferStore";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useShopStore from "@/store/useShopStore";
import { OfferType, SkinTypes } from "@/types/ui";
import { convertUnixTimeToRemainingSeconds } from "@/utils/format";
import {
	getWithExpiry,
	setWithExpiry,
	SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
} from "@/utils/inGameTimerOperations";
import { storeToRefs } from "pinia";
import { markRaw, onMounted, watch } from "vue";

let checkPackCooldownInterval: ReturnType<typeof setInterval> | null = null;
const questStore = useQuestStore();
const { tutorialActive } = storeToRefs(questStore);

const offerStore = useOfferStore();
const { specialBuildingPack } = storeToRefs(offerStore);

const shopStore = useShopStore();
const { hiddenLeftBoxOffers } = storeToRefs(shopStore);

const popupStore = usePopupStore();

onMounted((): void => {
	if (!specialBuildingPack.value) {
		loadSpecialBuildingPack();
	}
});

const getPremiumLeftBoxClass = (pack: InGameShopItem): string => {
	const base = "special-building-pack-left-box__item";
	return `${base}--${pack.store_id}-60`;
};

const openPopup = (): void => {
	popupStore.showPopup({
		id: `special-building-pack-${specialBuildingPack.value?.store_id}`,
		type: PopupType.INFO,
		isPaddingless: true,
		width: 24.5,
		height: "auto",
		skin: SkinTypes.DARK,
		forceDividerLine: true,
		hasOverlap: true,
		hasOuterBar: true,
		component: markRaw(SpecialBuildingPackPopup),
	});
};

const handleLeftboxEnd = (): void => {
	if (!specialBuildingPack.value) return;
	const endDate = new Date(specialBuildingPack.value.end_date as string);
	offerStore.clearSpecialBuildingPack();
	if (Date.now() < endDate.getTime() + SPECIAL_BUILDING_PACK_COOLDOWN) {
		setWithExpiry(
			SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
			SPECIAL_BUILDING_PACK_COOLDOWN,
		);
	}
};

const loadSpecialBuildingPack = (): void => {
	let specialBuildingPackCooldown = getWithExpiry(
		SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
	);
	if (specialBuildingPackCooldown) {
		checkPackCooldownInterval = setInterval((): void => {
			specialBuildingPackCooldown = getWithExpiry(
				SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
			);

			if (!specialBuildingPackCooldown) {
				if (checkPackCooldownInterval) {
					clearInterval(checkPackCooldownInterval);
					checkPackCooldownInterval = null;
					offerStore.setSpecialBuildingPack(true);
				}
			}
		}, 10_000);
	} else {
		if (checkPackCooldownInterval) {
			clearInterval(checkPackCooldownInterval);
			checkPackCooldownInterval = null;
		}
		offerStore.setSpecialBuildingPack(true);
	}
};

watch(
	(): InGameShopItem | null => specialBuildingPack.value,
	(value: InGameShopItem | null): void => {
		if (!value) {
			loadSpecialBuildingPack();
		}
	},
);
</script>

<style lang="scss" scoped>
.special-building-pack-left-box {
	position: relative;
	@include flex-col-center;
	align-items: flex-start;
	gap: 0.375rem;
	margin-left: 0.5rem;

	$sizes: (60, 70);

	$baseWidth: 140;
	$overlapWidth: 160;
	$baseHeight: 208;

	$premiumGemsBuildings: (
		"lok_gold_mine_premium_pack": 0,
		"lok_industrial_lumber_mill_pack": 1,
		"lok_ox_stable_pack": 2,
		"lok_universal_warehouse_pack": 3,
		"lok_large_stone_quarry_pack": 4,
		"lok_large_clay_pit_pack": 5,
		"lok_advanced_iron_mine_pack": 6,
		"lok_patisserie_pack": 7,
		"lok_carpentry_district_pack": 8,
		"lok_stonemasonry_district_pack": 9,
		"lok_tax_office_pack": 10,
		"lok_industrial_foundry_pack": 11,
		"lok_brickyard_pack": 12,
	);

	&__item {
		position: relative;
		cursor: pointer;
		@include clickable-element;

		&--overflow-normalized {
			left: -0.313rem;
		}

		.special-building-icon {
			position: absolute;
			bottom: 1.15rem;
			left: 0;
		}
	}

	@each $int in $sizes {
		$ratio: calc($int / $baseWidth);
		$overlapRatio: calc($int / $overlapWidth);

		// Gems

		$last_index_special: 0;

		@each $name, $index in $premiumGemsBuildings {
			$last_index: $index;
		}

		@each $name, $pos in $premiumGemsBuildings {
			.special-building-pack-left-box__item--#{$name}-#{$int} {
				display: inline-block;
				margin-top: 0.7rem;
				background-image: url($path-images + "shop/left-boxes/LB-premium-building.avif"); // TODO redo = we already have an item in the sprite for that
				// TODO ask the graphics why there is a different border here than in the new sprite where there is an item with dynamic tile icon allocation
				background-position: center;
				background-size: contain;
				background-repeat: no-repeat;
				width: 3.75rem;
				height: 4.5rem;
			}
		}

		.special-building-pack-left-box__valentine-item--lok_valentine_multipack-#{$int} {
			display: inline-block;
			background-image: url($path-images + "shop/left-boxes/leftbox-special-offers-sprite.avif");
			background-position: (-(math.div($int, 16)) * 0) * 1rem 0;
			background-size: (
					math.div((($overlapWidth * $overlapRatio) * (0 + 1)), 16) *
						1rem
				)
				(math.div(($baseHeight * $overlapRatio), 16) * 1rem);
			background-repeat: no-repeat;
			width: math.div($int, 16) * 1rem;
			height: math.div(calc($overlapRatio * $baseHeight), 16) * 1rem;
		}
	}

	&__timer,
	&__text {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 1.313rem;
		color: #f2dcb5;
		font-size: 0.75rem;
		text-align: center;
	}
	&__timer {
		height: 1.5rem;
	}
}
</style>
